/**
 * Display formatters — one way to print money, numbers, dates and ages across the app.
 *
 * Rules (approved UI overhaul):
 * - Empty / null / undefined → '—' (an em dash), never a blank cell.
 * - Numbers use thousands separators ("en-US" grouping, independent of the browser locale).
 * - Dates read '13 Aug 2026' (never mm/dd/yyyy, never ISO, never an Excel serial like 46362).
 * - Date-times read '28 Sep 2026, 10:42' in Gulf Standard Time (Asia/Dubai, UTC+4).
 * - Ages read '48 d'; relative days read '2 d ago' / 'in 16 d'.
 *
 * See docs/UI_FOUNDATION.md for usage.
 */

export const EMPTY = '—';
export const DEFAULT_TIME_ZONE = 'Asia/Dubai';
export const DEFAULT_DATE_FORMAT = 'd_mmm_yyyy';

/*
  Workspace defaults (Admin › Settings › Workspace defaults, 26 #8). AppSettingsContext calls
  configureFormatting({ timeZone, dateFormat }) with the company's choices as soon as GET /api/settings answers, so
  every date / time below follows them unless a caller passes its own `timeZone`:
    timeZone    an IANA zone ('Asia/Dubai', 'Africa/Cairo', …) — date-times, "today", ages, toDateInputValue
    dateFormat  'd_mmm_yyyy' 30 Sep 2026 · 'dd_mm_yyyy' 30/09/2026 · 'yyyy_mm_dd' 2026-09-30 · 'mmm_d_yyyy' Sep 30, 2026
  Money keeps its own `currency` option (AED by default): amounts are labelled AED in column headers and server
  texts too, so the workspace currency is not applied here yet.
*/
const DATE_FORMATS = new Set(['d_mmm_yyyy', 'dd_mm_yyyy', 'yyyy_mm_dd', 'mmm_d_yyyy']);
const formatting = { timeZone: DEFAULT_TIME_ZONE, dateFormat: DEFAULT_DATE_FORMAT };

const isTimeZone = (zone) => {
  if (typeof zone !== 'string' || !zone.trim()) return false;
  try {
    return Boolean(new Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions().timeZone);
  } catch {
    return false;
  }
};

/** Set the app-wide defaults (unknown values are ignored; `null` restores a built-in default). Returns them. */
export function configureFormatting({ timeZone, dateFormat } = {}) {
  if (timeZone === null) formatting.timeZone = DEFAULT_TIME_ZONE;
  else if (isTimeZone(timeZone)) formatting.timeZone = timeZone;
  if (dateFormat === null) formatting.dateFormat = DEFAULT_DATE_FORMAT;
  else if (DATE_FORMATS.has(dateFormat)) formatting.dateFormat = dateFormat;
  return { ...formatting };
}

/** The defaults in force: { timeZone, dateFormat }. */
export const formattingDefaults = () => ({ ...formatting });

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_MS = 24 * 60 * 60 * 1000;
// Excel serial dates (days since 1899-12-30) for roughly 1955–2118.
const EXCEL_SERIAL_MIN = 20000;
const EXCEL_SERIAL_MAX = 80000;
const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);

export const isBlank = (value) => (
  value === null
  || value === undefined
  || (typeof value === 'string' && value.trim() === '')
  || (typeof value === 'number' && Number.isNaN(value))
);

/* ------------------------------------------------------------------ numbers */

const CURRENCY = '(?:AED|USD|EUR|GBP|SAR|QAR|KWD|OMR|BHD|INR|DHS?|\\$|€|£)';
const CURRENCY_PREFIX = new RegExp(`^${CURRENCY}\\.?\\s*`, 'i');
const CURRENCY_SUFFIX = new RegExp(`\\s*${CURRENCY}\\.?$`, 'i');
const NUMERIC = /^[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?$/i;

/**
 * Parse numbers and numeric strings → number | null.
 *   '94,605.00' · 'AED 1,200' · '1,200 AED' · '-AED 1,200' · 'AED -1,200' · '(1,200)' (accounting
 *   negative) · ' 42 ' · '−5' (Unicode minus). Anything else → null.
 */
export function toNumber(value) {
  if (isBlank(value)) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value !== 'string') return null;
  let text = value.trim().replace(/[\u2212\u2013]/g, '-');
  let negative = false;
  const parens = text.match(/^\((.*)\)$/);
  if (parens) {
    negative = true;
    text = parens[1].trim();
  }
  const sign = text.match(/^([-+])\s*(?=\D)/); // a sign in front of a currency: '-AED 1,200'
  if (sign) {
    if (sign[1] === '-') negative = !negative;
    text = text.slice(sign[0].length);
  }
  text = text.replace(CURRENCY_PREFIX, '').replace(CURRENCY_SUFFIX, '').replace(/[\s,]/g, '');
  if (!NUMERIC.test(text)) return null;
  const parsed = Number(text);
  if (!Number.isFinite(parsed)) return null;
  return negative ? -parsed : parsed;
}

/** Fallback for a value that is not a number: non-empty text is shown as written (unless strict). */
const notANumber = (value, fallback, strict) => (
  !strict && typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback
);

const numberFormatters = new Map();
const getNumberFormatter = (minimumFractionDigits, maximumFractionDigits) => {
  const key = `${minimumFractionDigits}:${maximumFractionDigits}`;
  if (!numberFormatters.has(key)) {
    numberFormatters.set(key, new Intl.NumberFormat('en-US', { minimumFractionDigits, maximumFractionDigits }));
  }
  return numberFormatters.get(key);
};

/**
 * formatNumber(1234.5) → '1,234.5'
 * formatNumber(1234.5, { decimals: 2 }) → '1,234.50'
 * Options: decimals (fixed), or minDecimals (default 0) + maxDecimals (default 2); fallback ('—').
 * Text that is not a number ('TBD', 'see LPO') is returned as written so nothing silently
 * disappears; pass { strict: true } to get the fallback instead.
 */
export function formatNumber(value, {
  decimals, minDecimals = 0, maxDecimals = 2, fallback = EMPTY, strict = false,
} = {}) {
  const number = toNumber(value);
  if (number === null) return notANumber(value, fallback, strict);
  const min = decimals ?? minDecimals;
  const max = decimals ?? Math.max(maxDecimals, min);
  // Avoid "-0" / "-0.00" for values that round to zero.
  const safe = Number(number.toFixed(max)) === 0 ? 0 : number;
  return getNumberFormatter(min, max).format(safe);
}

/**
 * formatMoney(498855) → 'AED 498,855.00'
 * formatMoney(498855, { decimals: 0 }) → 'AED 498,855'        (KPIs / aggregates)
 * formatMoney(94605, { currency: null }) → '94,605.00'        (table cells under an "Amount (AED)" header)
 * formatMoney('(1,200)') → 'AED -1,200.00'; formatMoney('TBD') → 'TBD'; formatMoney('TBD', { strict: true }) → '—'
 * Options: currency ('AED' default, null/'' for none), decimals (default 2), fallback ('—'), strict.
 */
export function formatMoney(value, {
  currency = 'AED', decimals = 2, fallback = EMPTY, strict = false,
} = {}) {
  const number = toNumber(value);
  if (number === null) return notANumber(value, fallback, strict);
  const amount = formatNumber(number, { decimals });
  return currency ? `${currency} ${amount}` : amount;
}

/**
 * formatPercent(46) → '46%'; formatPercent(0.462, { ratio: true, decimals: 1 }) → '46.2%'
 * Non-numeric text is returned as written (unless strict), like formatNumber.
 */
export function formatPercent(value, {
  decimals = 0, ratio = false, fallback = EMPTY, strict = false,
} = {}) {
  const number = toNumber(value);
  if (number === null) return notANumber(value, fallback, strict);
  return `${formatNumber(ratio ? number * 100 : number, { decimals })}%`;
}

/* -------------------------------------------------------------------- dates */

const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

const fromExcelSerial = (serial) => {
  const date = new Date(EXCEL_EPOCH_UTC + Math.floor(serial) * DAY_MS);
  return { dateOnly: true, y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() };
};

const validYmd = (y, m, d) => {
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const check = new Date(Date.UTC(y, m - 1, d));
  if (check.getUTCMonth() !== m - 1) return null; // e.g. 31 Feb
  return { dateOnly: true, y, m, d };
};

// Epoch milliseconds the app can plausibly hold: 1973-03-03 … 2286-11-20. Anything else
// (0, 2026, epoch seconds like 1727700000) is not treated as a date.
const EPOCH_MS_MIN = 1e11;
const EPOCH_MS_MAX = 1e13;

const MONTH_INDEX = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
};
const MONTH_NAMES = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
  'september', 'october', 'november', 'december'];

/** 'Sep' / 'Sept.' / 'September' → 9; anything else → null. */
const monthFromName = (name) => {
  const word = name.toLowerCase().replace(/\.$/, '');
  const month = MONTH_INDEX[word] || MONTH_INDEX[word.slice(0, 3)];
  if (!month) return null;
  return MONTH_NAMES[month - 1].startsWith(word) || word === 'sept' ? month : null;
};

const WEEKDAY_PREFIX = '(?:[a-z]{3,9}\\.?,?\\s+)?';
const ORDINAL = '(?:st|nd|rd|th)?';
// 16 Sep 2026 · 16-Sep-2026 · Wed, 16 September, 2026 · 16th Sep 2026
const DAY_MONTH_YEAR = new RegExp(`^${WEEKDAY_PREFIX}(\\d{1,2})${ORDINAL}[\\s/.-]+([a-z]{3,9}\\.?),?[\\s/.-]+(\\d{4})$`, 'i');
// Sep 16, 2026 · Wed Sep 16 2026 · September 16th 2026
const MONTH_DAY_YEAR = new RegExp(`^${WEEKDAY_PREFIX}([a-z]{3,9}\\.?)\\s+(\\d{1,2})${ORDINAL},?\\s+(\\d{4})$`, 'i');

/** Calendar date of a Date's local wall clock (date pickers create local midnight). */
const localYmd = (date) => ({ dateOnly: true, y: date.getFullYear(), m: date.getMonth() + 1, d: date.getDate() });

const isLocalMidnight = (date) => (
  date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0
);

/**
 * Parse anything the app stores as a date. Returns
 *   { dateOnly: true, y, m, d }  for calendar dates (never shifted by a time zone), or
 *   { dateOnly: false, date }    for instants (formatted in Asia/Dubai), or null.
 *
 * Calendar dates: 'YYYY-MM-DD', 'YYYY/M/D' ('.' or '-' too), day-first 'DD/MM/YYYY', '16 Sep 2026',
 * 'Sep 16, 2026' (optional weekday / ordinal), Excel serials (46362), and Date objects at exactly
 * local midnight (what date pickers return — `new Date(2026, 8, 16)`).
 * Instants: ISO date-times (naive ones are UTC, the backend's convention), other Date objects,
 * epoch milliseconds, and other strings that carry a time and a 4-digit year.
 * Everything else — 'TBD', 'Block 3', 'Q3 2026', 0, 2026, epoch seconds — is null, so formatters show
 * the text as written (or the fallback) instead of a plausible but wrong date.
 */
export function parseDateValue(value) {
  if (isBlank(value)) return null;
  if (value instanceof Date) {
    if (!isValidDate(value)) return null;
    return isLocalMidnight(value) ? localYmd(value) : { dateOnly: false, date: new Date(value.getTime()) };
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    if (value >= EXCEL_SERIAL_MIN && value <= EXCEL_SERIAL_MAX) return fromExcelSerial(value);
    if (Math.abs(value) < EPOCH_MS_MIN || Math.abs(value) > EPOCH_MS_MAX) return null;
    const date = new Date(value);
    return isValidDate(date) ? { dateOnly: false, date } : null;
  }
  if (typeof value !== 'string') return null;
  const text = value.trim();

  // Year-first calendar dates: 2026-09-16, 2026/9/16, 2026.09.16
  let match = text.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/);
  if (match) return validYmd(Number(match[1]), Number(match[2]), Number(match[3]));

  match = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/i);
  if (match) {
    const iso = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6] || '00'}${match[7] || 'Z'}`;
    const date = new Date(iso.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
    return isValidDate(date) ? { dateOnly: false, date } : null;
  }

  // Day-first (UAE / en-GB): 16/09/2026, 16-09-2026, 16.09.2026
  match = text.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (match) return validYmd(Number(match[3]), Number(match[2]), Number(match[1]));

  if (/^\d{5}(\.\d+)?$/.test(text)) {
    const serial = Number(text);
    if (serial >= EXCEL_SERIAL_MIN && serial <= EXCEL_SERIAL_MAX) return fromExcelSerial(serial);
    return null;
  }

  if (/^\d+$/.test(text)) return null; // bare numbers are not dates

  // Month-name calendar dates, parsed by hand (Date.parse would treat them as local midnight
  // and invent years: new Date('16 Sep') is 2001).
  match = text.match(DAY_MONTH_YEAR);
  if (match) {
    const month = monthFromName(match[2]);
    return month ? validYmd(Number(match[3]), month, Number(match[1])) : null;
  }
  match = text.match(MONTH_DAY_YEAR);
  if (match) {
    const month = monthFromName(match[1]);
    return month ? validYmd(Number(match[3]), month, Number(match[2])) : null;
  }

  // Anything else must carry a time and a 4-digit year to be read as an instant
  // ('Wed Sep 16 2026 10:00:00 GMT+0400', 'Wed, 16 Sep 2026 06:00:00 GMT').
  if (!/\d:\d/.test(text) || !/(^|\D)\d{4}(\D|$)/.test(text)) return null;
  const date = new Date(text);
  return isValidDate(date) ? { dateOnly: false, date } : null;
}

/** Unparseable date: text is shown as written (nothing silently disappears); numbers, objects → fallback. */
const notADate = (value, fallback) => (
  typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback
);

const zonedFormatters = new Map();
const getZonedParts = (date, timeZone) => {
  const key = timeZone || 'local';
  if (!zonedFormatters.has(key)) {
    let formatter;
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone || undefined,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23',
      });
    } catch {
      formatter = new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
      });
    }
    zonedFormatters.set(key, formatter);
  }
  const parts = {};
  zonedFormatters.get(key).formatToParts(date).forEach(({ type, value }) => { parts[type] = value; });
  return {
    y: Number(parts.year),
    m: Number(parts.month),
    d: Number(parts.day),
    hh: Number(parts.hour) % 24,
    mm: Number(parts.minute),
  };
};

/** Calendar parts { y, m, d, hh?, mm? } of a parsed value in the given zone. */
const calendarParts = (parsed, timeZone) => (
  parsed.dateOnly ? { y: parsed.y, m: parsed.m, d: parsed.d } : getZonedParts(parsed.date, timeZone)
);

const dayNumber = ({ y, m, d }) => Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
const pad2 = (n) => String(n).padStart(2, '0');

/** One calendar day in the workspace date format (configureFormatting), with or without its year. */
function calendarText({ y, m, d }, showYear) {
  switch (formatting.dateFormat) {
    case 'dd_mm_yyyy':
      return `${pad2(d)}/${pad2(m)}${showYear ? `/${y}` : ''}`;
    case 'yyyy_mm_dd':
      return showYear ? `${y}-${pad2(m)}-${pad2(d)}` : `${pad2(d)} ${MONTHS[m - 1]}`;
    case 'mmm_d_yyyy':
      return `${MONTHS[m - 1]} ${d}${showYear ? `, ${y}` : ''}`;
    default:
      return `${d} ${MONTHS[m - 1]}${showYear ? ` ${y}` : ''}`;
  }
}

/**
 * formatDate('2026-08-13') → '13 Aug 2026'
 * Options: year 'always' (default) | 'auto' (omit when it is the current year) | 'never';
 *          weekday (prefix 'Wed, '); timeZone (default: the workspace zone, Asia/Dubai); now; fallback ('—').
 *          The day / month / year order follows the workspace date format (configureFormatting).
 * Unparseable text is returned as written so nothing silently disappears ('TBD' → 'TBD');
 * numbers that are not dates (0, 2026, epoch seconds) and other values give the fallback.
 */
export function formatDate(value, {
  year = 'always', weekday = false, timeZone = formatting.timeZone, now, fallback = EMPTY,
} = {}) {
  const parsed = parseDateValue(value);
  if (!parsed) return notADate(value, fallback);
  const parts = calendarParts(parsed, timeZone);
  let showYear = year !== 'never';
  if (year === 'auto') {
    showYear = parts.y !== getZonedParts(now ? new Date(now) : new Date(), timeZone).y;
  }
  const text = calendarText(parts, showYear);
  if (!weekday) return text;
  const dow = new Date(Date.UTC(parts.y, parts.m - 1, parts.d)).getUTCDay();
  return `${WEEKDAYS[dow]}, ${text}`;
}

/**
 * formatDateTime('2026-09-28T06:42:00Z') → '28 Sep 2026, 10:42'  (GST)
 * Options: relativeDay (→ 'Today, 10:42' / 'Yesterday, 21:04'), year, timeZone, now, fallback.
 * Date-only input prints just the date.
 */
export function formatDateTime(value, {
  relativeDay = false, year = 'always', timeZone = formatting.timeZone, now, fallback = EMPTY,
} = {}) {
  const parsed = parseDateValue(value);
  if (!parsed) return notADate(value, fallback);
  if (parsed.dateOnly) return formatDate(value, { year, timeZone, now, fallback });
  const parts = getZonedParts(parsed.date, timeZone);
  const time = `${pad2(parts.hh)}:${pad2(parts.mm)}`;
  if (relativeDay) {
    const diff = dayNumber(getZonedParts(now ? new Date(now) : new Date(), timeZone)) - dayNumber(parts);
    if (diff === 0) return `Today, ${time}`;
    if (diff === 1) return `Yesterday, ${time}`;
  }
  return `${formatDate(value, { year, timeZone, now })}, ${time}`;
}

/** formatTime('2026-09-28T17:04:00Z') → '21:04' (GST). */
export function formatTime(value, { timeZone = formatting.timeZone, fallback = EMPTY } = {}) {
  const parsed = parseDateValue(value);
  if (!parsed || parsed.dateOnly) return parsed ? fallback : notADate(value, fallback);
  const parts = getZonedParts(parsed.date, timeZone);
  return `${pad2(parts.hh)}:${pad2(parts.mm)}`;
}

/* --------------------------------------------------------- ages / relative */

/**
 * Whole calendar days from `value` to today (Asia/Dubai). Positive = in the past,
 * negative = in the future, null when there is no date.
 * ageDays('2026-08-13', { now: '2026-09-30' }) → 48
 */
export function ageDays(value, { now, timeZone = formatting.timeZone } = {}) {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  const today = getZonedParts(now ? new Date(now) : new Date(), timeZone);
  return dayNumber(today) - dayNumber(calendarParts(parsed, timeZone));
}

/** formatDays(48) → '48 d' (absolute value; '—' when empty). */
export function formatDays(days, { fallback = EMPTY } = {}) {
  const number = toNumber(days);
  if (number === null) return fallback;
  return `${formatNumber(Math.abs(Math.round(number)), { decimals: 0 })} d`;
}

/** formatAge('2026-08-13', { now }) → '48 d' — how long ago a date was. */
export function formatAge(value, options = {}) {
  const days = ageDays(value, options);
  return days === null ? (options.fallback ?? EMPTY) : formatDays(Math.max(days, 0));
}

/**
 * formatRelativeDays(date) → 'today' | '2 d ago' | 'in 16 d'
 * style 'due' → 'due today' | '48 d late' | '92 d left' (deadlines).
 */
export function formatRelativeDays(value, { style = 'ago', now, timeZone, fallback = EMPTY } = {}) {
  const days = ageDays(value, { now, timeZone });
  if (days === null) return fallback;
  const count = formatDays(days);
  if (style === 'due') {
    if (days === 0) return 'due today';
    return days > 0 ? `${count} late` : `${count} left`;
  }
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days === -1) return 'tomorrow';
  return days > 0 ? `${count} ago` : `in ${count}`;
}

/**
 * formatTimeAgo(instant) → 'just now' | '2 min ago' | '3 h ago' | 'yesterday' | '5 d ago' | '13 Aug 2026'
 * For "Updated 2 min ago" style meta text.
 */
export function formatTimeAgo(value, { now, timeZone = formatting.timeZone, fallback = EMPTY } = {}) {
  const parsed = parseDateValue(value);
  if (!parsed) return notADate(value, fallback);
  if (parsed.dateOnly) return formatRelativeDays(value, { now, timeZone, fallback });
  const nowDate = now ? new Date(now) : new Date();
  const seconds = Math.round((nowDate.getTime() - parsed.date.getTime()) / 1000);
  if (seconds < 0) return formatDateTime(value, { relativeDay: true, timeZone, now });
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = ageDays(value, { now, timeZone });
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} d ago`;
  return formatDate(value, { timeZone, now });
}

/** Value for <input type="date"> ('YYYY-MM-DD') from any supported date value, or ''. */
export function toDateInputValue(value, { timeZone = formatting.timeZone } = {}) {
  const parsed = parseDateValue(value);
  if (!parsed) return '';
  const { y, m, d } = calendarParts(parsed, timeZone);
  return `${y}-${pad2(m)}-${pad2(d)}`;
}
